import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ANALYTICAL_UNIVERSE,
  CITATION_PLAIN,
  CONTRIBUTIONS,
  IE_FRAMING,
  LICENSE_STATEMENT,
  ManifestDoc,
  PRIMARY_RESULTS,
  PROVENANCE_STATEMENT,
  PUBLIC_AGENCIES,
  RELEASE_CONTENTS,
  REPRO_STATUS,
  RESEARCH_REPO,
  SCOPE_NOTES,
  STUDY_OVERVIEW,
  TEMPORAL_COMPARISONS,
  TEMPORAL_INTERPRETATION,
} from '../../../constants/solidWasteResearchRepo';
import './MunicipalLandfillMethanePage.css';

const BASE = `${process.env.PUBLIC_URL || ''}/research/municipal-landfill-methane-responsibility`;

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function groupFiles(files: ManifestDoc['files'], types: string[]) {
  return files.filter((f) => types.includes(f.type));
}

export const MunicipalLandfillMethanePage: React.FC = () => {
  const [manifest, setManifest] = useState<ManifestDoc | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    const pageTitle = 'Routing-Resolved Municipal Landfill Methane Responsibility | ClimatePlans Research';
    document.title = pageTitle;
    const descText =
      'Routing-resolved allocation of municipal landfill methane using California jurisdiction-to-landfill flows, facility-year emissions, and alternative temporal accounting conventions.';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', descText);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = RESEARCH_REPO.canonicalUrl;

    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.content = content;
    };
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:url', RESEARCH_REPO.canonicalUrl);
    setMeta('property', 'og:title', pageTitle);
    setMeta('property', 'og:description', descText);
    setMeta('property', 'og:image', `${window.location.origin}/images/climateplans-og-image.png`);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', pageTitle);
    setMeta('name', 'twitter:description', descText);

    const scriptId = 'solid-waste-research-jsonld';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Dataset',
          name: RESEARCH_REPO.title,
          description: descText,
          author: {
            '@type': 'Person',
            name: 'Christopher M. Jones',
            affiliation: RESEARCH_REPO.affiliation,
            identifier: RESEARCH_REPO.orcidUrl,
          },
          datePublished: RESEARCH_REPO.releaseDate,
          version: '1.0.0',
          identifier: RESEARCH_REPO.doiUrl,
          sameAs: [RESEARCH_REPO.zenodoUrl, RESEARCH_REPO.doiUrl],
          url: RESEARCH_REPO.canonicalUrl,
          license: 'https://creativecommons.org/licenses/by/4.0/',
          keywords:
            'landfill methane, municipal solid waste, greenhouse-gas inventories, material-flow analysis, industrial ecology, responsibility allocation',
          spatialCoverage: { '@type': 'Place', name: 'California' },
          temporalCoverage: '2019',
        },
        {
          '@type': 'SoftwareSourceCode',
          name: 'ClimatePlans SW-01 municipal landfill methane analysis scripts',
          programmingLanguage: 'Python',
          author: {
            '@type': 'Person',
            name: 'Christopher M. Jones',
            identifier: RESEARCH_REPO.orcidUrl,
          },
          version: '1.0.0',
          url: RESEARCH_REPO.canonicalUrl,
          license: 'https://opensource.org/licenses/MIT',
          identifier: RESEARCH_REPO.doiUrl,
        },
      ],
    });

    fetch(`${BASE}/manifest.json`)
      .then((r) => r.json())
      .then(setManifest)
      .catch(() => setManifest(null));
  }, []);

  const dataFiles = useMemo(
    () => (manifest ? groupFiles(manifest.files, ['data']) : []),
    [manifest]
  );
  const codeFiles = useMemo(
    () => (manifest ? groupFiles(manifest.files, ['code']) : []),
    [manifest]
  );
  const figureFiles = useMemo(
    () => (manifest ? groupFiles(manifest.files, ['figure']) : []),
    [manifest]
  );

  const copyText = useCallback(async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      setCopied(null);
    }
  }, []);

  const renderDownloadCard = (f: ManifestDoc['files'][0]) => (
    <li key={f.download_path} className="trepo-dl-item">
      <a href={f.download_path} className="trepo-dl-link" download>
        <span className="trepo-dl-title">{f.title}</span>
        <span className="trepo-dl-meta">
          {f.filename.split('.').pop()?.toUpperCase()} · {formatBytes(f.size_bytes ?? 0)} ·{' '}
          {f.version ?? RESEARCH_REPO.release}
        </span>
      </a>
    </li>
  );

  return (
    <article className="trepo-page" aria-labelledby="trepo-title">
      <header className="trepo-hero">
        <p className="trepo-kicker">ClimatePlans Research Repository</p>
        <h1 id="trepo-title" className="trepo-h1">
          {RESEARCH_REPO.title}
        </h1>
        <p className="trepo-authors">
          {RESEARCH_REPO.authors.join(' · ')} ·{' '}
          <a href={RESEARCH_REPO.orcidUrl} rel="noopener noreferrer">
            ORCID {RESEARCH_REPO.orcid}
          </a>
        </p>
        <p className="trepo-authors">{RESEARCH_REPO.affiliation}</p>
        <div className="trepo-badges">
          <span className="trepo-badge">{RESEARCH_REPO.statusBadge}</span>
          <span className="trepo-badge">
            Release {RESEARCH_REPO.release} · {RESEARCH_REPO.releaseDateDisplay}
          </span>
        </div>
        <p className="trepo-meta-line">
          <span>{RESEARCH_REPO.journal} — {RESEARCH_REPO.articleType}</span>
          <span>{RESEARCH_REPO.editorialDepartment}</span>
          <span>
            DOI{' '}
            <a href={RESEARCH_REPO.doiUrl} rel="noopener noreferrer">
              {RESEARCH_REPO.doi}
            </a>
          </span>
        </p>
        <div className="trepo-hero-actions">
          <a
            className="trepo-btn trepo-btn--primary"
            href={RESEARCH_REPO.doiUrl}
            rel="noopener noreferrer"
          >
            View archived release
          </a>
          <a
            className="trepo-btn trepo-btn--secondary"
            href={RESEARCH_REPO.zenodoUrl}
            rel="noopener noreferrer"
          >
            View Zenodo record
          </a>
          <a
            className="trepo-btn trepo-btn--secondary"
            href={RESEARCH_REPO.zenodoDownloadUrl}
            rel="noopener noreferrer"
          >
            Download release archive
          </a>
          <button
            type="button"
            className="trepo-btn trepo-btn--secondary"
            onClick={() => copyText(RESEARCH_REPO.doiUrl, 'doi')}
          >
            {copied === 'doi' ? 'Copied DOI' : 'Copy DOI'}
          </button>
          <button
            type="button"
            className="trepo-btn trepo-btn--secondary"
            onClick={() => copyText(CITATION_PLAIN, 'cite')}
          >
            {copied === 'cite' ? 'Copied' : 'Copy citation'}
          </button>
        </div>
      </header>

      <section className="trepo-section" aria-labelledby="trepo-overview">
        <h2 id="trepo-overview">Overview</h2>
        <p className="trepo-lead">{STUDY_OVERVIEW}</p>
        <p>{IE_FRAMING}</p>
        <div className="trepo-cards">
          {CONTRIBUTIONS.map((c) => (
            <div key={c.title} className="trepo-card">
              <h3>{c.title}</h3>
              <p>{c.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="trepo-section" aria-labelledby="trepo-universe">
        <h2 id="trepo-universe">Analytical universe</h2>
        <dl className="trepo-findings">
          {ANALYTICAL_UNIVERSE.map((f) => (
            <div key={f.label} className="trepo-finding">
              <dt>{f.label}</dt>
              <dd>{f.value}</dd>
            </div>
          ))}
        </dl>
        <p className="trepo-note">
          94.69% is U1/U0 within the routed network, not statewide disposal coverage.
        </p>
      </section>

      <section className="trepo-section" aria-labelledby="trepo-results">
        <h2 id="trepo-results">Primary results</h2>
        <dl className="trepo-findings">
          {PRIMARY_RESULTS.map((f) => (
            <div key={f.label} className="trepo-finding">
              <dt>{f.label}</dt>
              <dd>{f.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="trepo-section" aria-labelledby="trepo-temporal">
        <h2 id="trepo-temporal">Temporal-accounting comparisons</h2>
        <dl className="trepo-findings">
          {TEMPORAL_COMPARISONS.map((f) => (
            <div key={f.label} className="trepo-finding">
              <dt>{f.label}</dt>
              <dd>{f.value}</dd>
            </div>
          ))}
        </dl>
        <p className="trepo-note">{TEMPORAL_INTERPRETATION}</p>
      </section>

      <section className="trepo-section trepo-restrict" aria-labelledby="trepo-scope">
        <h2 id="trepo-scope">Scope and interpretation</h2>
        <ul className="trepo-links">
          {SCOPE_NOTES.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      </section>

      <section className="trepo-section" aria-labelledby="trepo-downloads">
        <h2 id="trepo-downloads">Data, code, and release contents</h2>
        <p>
          The v1.0.0 release archive includes {RELEASE_CONTENTS.join('; ').toLowerCase()}.
          Reproduction uses archived local inputs and scripts without Supabase.
        </p>
        <div className="trepo-dl-grid">
          <div className="trepo-dl-group">
            <h3>Archive</h3>
            <ul>
              <li className="trepo-dl-item">
                <a href={RESEARCH_REPO.doiUrl} className="trepo-dl-link" rel="noopener noreferrer">
                  <span className="trepo-dl-title">Zenodo archived release (primary)</span>
                  <span className="trepo-dl-meta">{RESEARCH_REPO.archiveFilename}</span>
                </a>
              </li>
              <li className="trepo-dl-item">
                <a
                  href={RESEARCH_REPO.zenodoDownloadUrl}
                  className="trepo-dl-link"
                  rel="noopener noreferrer"
                >
                  <span className="trepo-dl-title">Direct archive download</span>
                </a>
              </li>
              <li className="trepo-dl-item">
                <a href={`${BASE}/CITATION.cff`} className="trepo-dl-link" download>
                  <span className="trepo-dl-title">CITATION.cff</span>
                </a>
              </li>
              <li className="trepo-dl-item">
                <a href={`${BASE}/checksums.sha256`} className="trepo-dl-link" download>
                  <span className="trepo-dl-title">SHA-256 checksums</span>
                </a>
              </li>
            </ul>
          </div>
          <div className="trepo-dl-group">
            <h3>Analytical data</h3>
            <ul>{dataFiles.map(renderDownloadCard)}</ul>
          </div>
          <div className="trepo-dl-group">
            <h3>Figures</h3>
            <ul>{figureFiles.map(renderDownloadCard)}</ul>
          </div>
          <div className="trepo-dl-group">
            <h3>Code and documentation</h3>
            <ul>
              {codeFiles.map(renderDownloadCard)}
              <li className="trepo-dl-item">
                <a
                  href={`${BASE}/releases/${RESEARCH_REPO.release}/documentation/REPRODUCIBILITY.md`}
                  className="trepo-dl-link"
                  download
                >
                  <span className="trepo-dl-title">Reproducibility guide</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
        <p className="trepo-note">
          Archive SHA-256: <code>{RESEARCH_REPO.archiveSha256}</code>
        </p>
      </section>

      <section className="trepo-section trepo-restrict" aria-labelledby="trepo-data">
        <h2 id="trepo-data">Data availability, license, and provenance</h2>
        <p>{LICENSE_STATEMENT}</p>
        <p>{PROVENANCE_STATEMENT}</p>
        <ul className="trepo-agencies">
          {PUBLIC_AGENCIES.map((a) => (
            <li key={a.url}>
              <a href={a.url} rel="noopener noreferrer">
                {a.name}
              </a>
            </li>
          ))}
        </ul>
      </section>

      <section className="trepo-section" aria-labelledby="trepo-repro">
        <h2 id="trepo-repro">Reproducibility</h2>
        <table className="trepo-table">
          <caption className="trepo-sr-only">Reproducibility status by component</caption>
          <thead>
            <tr>
              <th scope="col">Component</th>
              <th scope="col">Reproducible from archived release</th>
              <th scope="col">Notes</th>
            </tr>
          </thead>
          <tbody>
            {REPRO_STATUS.map(([comp, yes, note]) => (
              <tr key={comp}>
                <td>{comp}</td>
                <td>{yes}</td>
                <td>{note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="trepo-section" aria-labelledby="trepo-versions">
        <h2 id="trepo-versions">Release version</h2>
        <div className="trepo-version-card">
          <h3>
            Version {RESEARCH_REPO.release} — {RESEARCH_REPO.releaseLabel}
          </h3>
          <ul>
            <li>
              <strong>Release date:</strong> {RESEARCH_REPO.releaseDateDisplay}
            </li>
            <li>
              <strong>Status:</strong> {RESEARCH_REPO.statusBadge}
            </li>
            <li>
              <strong>DOI:</strong>{' '}
              <a href={RESEARCH_REPO.doiUrl} rel="noopener noreferrer">
                {RESEARCH_REPO.doiUrl}
              </a>
            </li>
            <li>
              <strong>Zenodo:</strong>{' '}
              <a href={RESEARCH_REPO.zenodoUrl} rel="noopener noreferrer">
                {RESEARCH_REPO.zenodoUrl}
              </a>
            </li>
            <li>
              <strong>Archive SHA-256:</strong> {RESEARCH_REPO.archiveSha256}
            </li>
          </ul>
        </div>
      </section>

      <section className="trepo-section" aria-labelledby="trepo-cite">
        <h2 id="trepo-cite">Citation</h2>
        <pre className="trepo-pre">{CITATION_PLAIN}</pre>
        <button
          type="button"
          className="trepo-btn trepo-btn--small"
          onClick={() => copyText(CITATION_PLAIN, 'article')}
        >
          {copied === 'article' ? 'Copied' : 'Copy citation'}
        </button>
        <p className="trepo-note">
          <a href={`${BASE}/CITATION.cff`} download>
            Download CITATION.cff
          </a>
        </p>
      </section>

      <section className="trepo-section" aria-labelledby="trepo-funding">
        <h2 id="trepo-funding">Funding</h2>
        <p>{RESEARCH_REPO.funding}</p>
      </section>

      <section className="trepo-section" aria-labelledby="trepo-related">
        <h2 id="trepo-related">Related resources</h2>
        <ul className="trepo-links">
          <li>
            <Link to="/">ClimatePlans homepage</Link>
          </li>
          <li>
            <a href={RESEARCH_REPO.zenodoUrl} rel="noopener noreferrer">
              Zenodo archived release
            </a>
          </li>
        </ul>
      </section>
    </article>
  );
};

export default MunicipalLandfillMethanePage;
